import React from "react"
import { cn } from "@/lib/utils"
import { ArrowRightIcon, MailIcon, CheckCircleIcon, XCircleIcon } from "lucide-react"

interface EmailLogEntry {
  id: string
  mailerClass: string
  mailerAction: string
  subject: string
  toAddress: string
  status: "sent" | "failed"
  sentAt: string
}

interface RecentEmailLogsProps {
  emailLogs?: EmailLogEntry[]
  viewAllHref?: string
  title?: string
  className?: string
}

const getStatusIcon = (status: EmailLogEntry["status"]) => {
  return status === "sent" ? CheckCircleIcon : XCircleIcon
}

const getStatusColor = (status: EmailLogEntry["status"]) => {
  return status === "sent"
    ? "text-green-600 dark:text-green-500"
    : "text-red-600 dark:text-red-500"
}

export function RecentEmailLogs({
  emailLogs = [],
  viewAllHref = "/admin/email_logs",
  title = "Recent Emails Sent",
  className
}: RecentEmailLogsProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">
            Latest transactional emails
          </p>
        </div>
        <a
          href={viewAllHref}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
        >
          View All
          <ArrowRightIcon className="w-4 h-4" />
        </a>
      </div>

      {/* Email logs list */}
      <div className="space-y-3">
        {emailLogs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MailIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No emails sent yet</p>
          </div>
        ) : (
          emailLogs.map((log) => {
            const StatusIcon = getStatusIcon(log.status)
            return (
              <div
                key={log.id}
                className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                {/* Mail icon */}
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MailIcon className="w-5 h-5 text-primary" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {log.subject || "(No subject)"}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        To: {log.toAddress}
                      </p>
                    </div>
                    <StatusIcon className={cn("w-4 h-4 flex-shrink-0 mt-0.5", getStatusColor(log.status))} />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-muted-foreground">
                      {log.sentAt}
                    </p>
                    <span className="text-xs text-muted-foreground">&middot;</span>
                    <p className="text-xs text-muted-foreground">
                      {log.mailerAction}
                    </p>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
